// ── SmartTraQ Auth (JoinForm, LoginPage) ──
import React, { useState } from "react";
import { HF, RLS } from './constants';
import { getProfile } from './db.js';
import supabase from './supabase.js';

function JoinForm({orgName,code,routeOrgId,onIn,users,onLocalSignUp,onSupaIn,sett:joinSett,OrgSetupModal}){
  const [mode,setMode]=useState("up"); // up=signup, in=signin
  const [nm,setNm]=useState("");
  const [em,setEm]=useState("");
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const [ld,setLd]=useState(false);
  const inputSt={width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,.12)",background:"rgba(255,255,255,.06)",color:"#FFF",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:13,outline:"none"};

  // Parse role from join code (format: "role-RANDOM")
  const parseRoleFromCode=(c)=>{
    if(!c)return "viewer";
    const dash=c.indexOf("-");
    if(dash>0){const r=c.substring(0,dash);const validRoles=["admin","procurement","logistics","sales","finance","viewer","member"];if(validRoles.includes(r))return r}
    return "viewer";
  };

  // Find matching invite by code or email (from admin's sett — may not be available on visitor's browser)
  const findInvite=(email)=>{
    const invites=joinSett?.invites||[];
    return invites.find(i=>i.status==="pending"&&(i.code===code||(email&&i.email?.toLowerCase()===email.toLowerCase())));
  };

  // Create profile linked to the inviter's org
  const createJoinProfile=async(authUser,name,email)=>{
    if(!supabase)return null;
    const inv=findInvite(email);
    const role=inv?.role||parseRoleFromCode(code);
    const initials=name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2);
    // Find the org_id — try multiple approaches
    let orgId=routeOrgId||joinSett?.orgId||null;
    // Method 1: Try RPC if available (bypasses RLS)
    if(!orgId){
      try{
        const{data}=await supabase.rpc("find_org_by_name",{p_name:orgName});
        if(data)orgId=data;
      }catch{}
    }
    // Method 2: Direct query exact match
    if(!orgId){
      try{
        const{data,error}=await supabase.from("organizations").select("id").eq("name",orgName).maybeSingle();
        if(data)orgId=data.id;
      }catch{}
    }
    // Method 3: Removed — fuzzy ilike match was unnecessary attack surface
    // Method 4: Removed — generic profile lookup leaked org membership
    if(!orgId){
      console.error("createJoinProfile: Could not find org for:",orgName);
      setErr("Could not find organization '"+orgName+"'. Ask your admin to resend the invite link.");
      return null;
    }
    // Clean up stale profiles with same email but different ID (happens when user deleted from auth then re-invited)
    try{
      const{data:stale}=await supabase.from("profiles").select("id").eq("email",email).neq("id",authUser.id);
      if(stale?.length>0){
        await supabase.from("profiles").delete().in("id",stale.map(s=>s.id));
        
      }
    }catch(e){console.warn("Stale profile cleanup skipped:",e);}
    // Upsert profile (handles re-invite after removal)
    try{
      const profileData={
        id:authUser.id, org_id:orgId, name, email,
        role, initials, active:true, updated_at:new Date().toISOString()
      };
      const{data,error}=await supabase.from("profiles").upsert(profileData,{onConflict:"id"}).select("*, organizations(*)").single();
      if(error){
        console.error("Profile upsert error:",error.message,error.code);
        // Try insert instead of upsert
        const{data:d2,error:e2}=await supabase.from("profiles").insert(profileData).select("*, organizations(*)").single();
        if(e2){
          console.error("Profile insert error:",e2.message,e2.code);
          return {profile:null,errMsg:"Profile creation failed: "+(e2.message||"Unknown error")+" (code:"+e2.code+"). Contact your admin."};
        }
        return {profile:d2,errMsg:null};
      }
      return {profile:data,errMsg:null};
    }catch(e){
      console.error("Profile create error:",e);
      return {profile:null,errMsg:"Profile creation failed: "+e.message+". Contact your admin."};
    }
  };

  const go=async()=>{
    setErr("");setLd(true);
    if(mode==="up"){
      if(!nm.trim()){setErr("Enter your name");setLd(false);return}
      if(!em.trim()||!em.includes("@")){setErr("Enter a valid email");setLd(false);return}
      if(pw.length<6){setErr("Password must be at least 6 characters");setLd(false);return}
      if(supabase){
        try{
          const{data,error}=await supabase.auth.signUp({email:em.trim(),password:pw,options:{data:{full_name:nm.trim()}}});
          if(error){
            const msg=error.message||"";
            // Handle "already registered" — try signing in instead
            if(msg.toLowerCase().includes("already")||msg.toLowerCase().includes("registered")||msg.toLowerCase().includes("exists")){
              setErr("This email is already registered. Signing you in...");
              // Auto-attempt sign in
              const{data:siData,error:siErr}=await supabase.auth.signInWithPassword({email:em.trim(),password:pw});
              if(siErr){setErr("Account exists but password doesn't match. Try signing in with your existing password.");setMode("in");setLd(false);return}
              if(siData?.user){
                // Check if profile exists
                let profile=await getProfile();
                if(!profile){
                  const{profile:rp,errMsg:re}=await createJoinProfile(siData.user,nm.trim()||em.split("@")[0],em.trim());
                  profile=rp;
                  if(!rp){setErr(re||"Could not set up your profile. Contact your admin.");setLd(false);return}
                }
                if(profile){onSupaIn&&onSupaIn(siData.user,profile);setLd(false);return}
                setErr("Could not set up your profile. Contact your admin.");
                setLd(false);return;
              }
            }
            setErr(msg);setLd(false);return;
          }
          if(data?.user){
            // Ghost user check: Supabase returns fake success (empty identities, no session)
            // when email was previously deleted from auth.users then re-registered
            const isGhost=!data.session||data.user.identities?.length===0;
            if(isGhost){
              // This email still exists in Supabase's system — try signing in with new password
              setErr("Signing you in with your new account...");
              const{data:siData,error:siErr}=await supabase.auth.signInWithPassword({email:em.trim(),password:pw});
              if(siErr){
                // Can't sign in — the old account still exists with different password
                setErr("Your email was previously registered in our system. Please use the Sign In tab and enter your original password. If you've forgotten it, ask your admin to fully delete your account from Supabase dashboard (Authentication → Users → Delete).");
                setMode("in");setLd(false);return;
              }
              if(siData?.user){
                const{profile:p2,errMsg:e2}=await createJoinProfile(siData.user,nm.trim()||em.split("@")[0],em.trim());
                if(p2){onSupaIn&&onSupaIn(siData.user,p2);setLd(false);return}
                setErr(e2||"Signed in but profile setup failed. Contact your admin.");
                setLd(false);return;
              }
              setErr("Could not complete sign-in. Please use the Sign In tab.");
              setMode("in");setLd(false);return;
            }
            // Genuine new user created — create their profile linked to the org
            const{profile:newP,errMsg:newErr}=await createJoinProfile(data.user,nm.trim(),em.trim());
            if(newP){onSupaIn&&onSupaIn(data.user,newP);setLd(false);return}
            // Fallback: try getProfile in case trigger auto-created it
            const existingProfile=await getProfile();
            if(existingProfile){onSupaIn&&onSupaIn(data.user,existingProfile);setLd(false);return}
            setErr(newErr||"Account created but profile setup failed. Contact your admin.");
            setLd(false);return;
          }
        }catch(e){setErr(e.message);setLd(false);return}
      }
      // Local fallback
      const av=nm.split(" ").map(w=>w[0]).join("").substring(0,2).toUpperCase();
      const inv=findInvite(em.trim());
      const newUser={id:"U-"+Date.now(),name:nm.trim(),email:em.trim(),pw,role:inv?.role||"member",av,on:true};
      onLocalSignUp(newUser,orgName);
    }else{
      // Sign in mode
      if(!em.trim()){setErr("Enter your email");setLd(false);return}
      if(!pw){setErr("Enter your password");setLd(false);return}
      if(supabase){
        try{
          const{data,error}=await supabase.auth.signInWithPassword({email:em.trim(),password:pw});
          if(error){setErr(error.message);setLd(false);return}
          if(data?.user){
            let profile=await getProfile();
            if(!profile){
              const{profile:sp,errMsg:se}=await createJoinProfile(data.user,data.user.user_metadata?.full_name||em.split("@")[0],em.trim());
              profile=sp;
              if(!sp){setErr(se||"Signed in but profile setup failed. Contact your admin.");setLd(false);return}
            }
            if(profile){onSupaIn&&onSupaIn(data.user,profile);setLd(false);return}
            setErr("Signed in but no profile found. Contact your admin.");
            setLd(false);return;
          }
        }catch(e){setErr(e.message);setLd(false);return}
      }
      const found=users.find(u=>u.email===em.trim()&&u.pw===pw);
      if(found){onIn(found)}else{setErr("Invalid email or password")}
    }
    setLd(false);
  };
  return(<div>
    <div style={{display:"flex",gap:3,background:"rgba(255,255,255,.06)",borderRadius:10,padding:3,marginBottom:20}}>
      <button onClick={()=>{setMode("up");setErr("")}} style={{flex:1,padding:"10px 0",fontSize:13,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",borderRadius:8,background:mode==="up"?"rgba(255,255,255,.12)":"transparent",color:mode==="up"?"#FFF":"#94A3B8"}}>Create Account</button>
      <button onClick={()=>{setMode("in");setErr("")}} style={{flex:1,padding:"10px 0",fontSize:13,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",borderRadius:8,background:mode==="in"?"rgba(255,255,255,.12)":"transparent",color:mode==="in"?"#FFF":"#94A3B8"}}>Sign In</button>
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      {mode==="up"&&<div><label style={{fontSize:11,fontWeight:600,color:"#94A3B8",display:"block",marginBottom:5}}>FULL NAME</label><input value={nm} onChange={e=>setNm(e.target.value)} style={inputSt} placeholder="Your name"/></div>}
      <div><label style={{fontSize:11,fontWeight:600,color:"#94A3B8",display:"block",marginBottom:5}}>EMAIL</label><input type="email" value={em} onChange={e=>setEm(e.target.value)} style={inputSt} placeholder="you@company.com"/></div>
      <div><label style={{fontSize:11,fontWeight:600,color:"#94A3B8",display:"block",marginBottom:5}}>PASSWORD</label><input type="password" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")go()}} style={inputSt} placeholder={mode==="up"?"Min 6 characters":"Enter your password"}/></div>
    </div>
    {err&&<div style={{marginTop:10,padding:"8px 12px",borderRadius:8,background:err.includes("Signing you in")?"rgba(59,130,246,.15)":"rgba(239,68,68,.15)",border:"1px solid "+(err.includes("Signing you in")?"rgba(59,130,246,.25)":"rgba(239,68,68,.25)"),fontSize:11,color:err.includes("Signing you in")?"#93C5FD":"#FCA5A5"}}>{err}</div>}
    <button onClick={go} disabled={ld} style={{marginTop:16,width:"100%",padding:"13px 0",borderRadius:10,border:"none",background:ld?"rgba(255,255,255,.1)":"linear-gradient(135deg,#1E40AF,#0E7490)",color:"#FFF",fontFamily:"'Inter',system-ui,-apple-system,sans-serif",fontSize:14,fontWeight:700,cursor:ld?"wait":"pointer",boxShadow:ld?"none":"0 4px 16px rgba(30,64,175,.35)"}}>
      {ld?<span style={{display:"inline-flex",alignItems:"center",gap:8}}><span style={{width:14,height:14,border:"2px solid rgba(255,255,255,.3)",borderTopColor:"#FFF",borderRadius:"50%",animation:"spin .6s linear infinite",display:"inline-block"}}/>{mode==="up"?"Creating account...":"Signing in..."}</span>:mode==="up"?"Join "+orgName:"Sign In to "+orgName}
    </button>
  </div>);
}

function LoginPage({onIn,users,onSupaIn,onLocalSignUp,defaultTab,onBack,hideSignUp}) {
  const [tab,setTab] = useState(defaultTab==="up"?"up":"in");
  const [e,sE] = useState(""); const [p,sP] = useState(""); const [nm,sNm] = useState(""); const [org,sOrg] = useState("");
  const [err,sErr] = useState(""); const [ld,sLd] = useState(false); const [success,setSuccess] = useState("");
  const [newSignupUser,setNewSignupUser] = useState(null); // {authUser, orgName} — triggers org setup step 2

  const goSignIn = async () => {
    sErr(""); sLd(true); setSuccess("");
    if(!e.trim()||!e.includes("@")){sErr("Enter a valid email address");sLd(false);return}
    if(!p){sErr("Enter your password");sLd(false);return}
    try {
      if (supabase) {
        const { data, error } = await supabase.auth.signInWithPassword({ email: e, password: p });
        if (error) {
          const msg=error.message||"";
          if(msg.includes("Email not confirmed"))sErr("Please confirm your email first. Check your inbox for a verification link.");
          else if(msg.includes("Invalid login"))sErr("Invalid email or password. Check your credentials and try again.");
          else sErr(msg||"Sign in failed");
          sLd(false); return;
        }
        if (data?.user) {
          const profile = await getProfile();
          if (profile) { onSupaIn(data.user, profile); return; }
          sErr("Account exists but no workspace profile found. Contact your admin.");
          sLd(false); return;
        }
      }
      const u = users.find(x => x.email === e && x.pw === p && x.on);
      if (u) { onIn(u); return; }
      sErr("Invalid email or password"); sLd(false);
    } catch (ex) {
      const u = users.find(x => x.email === e && x.pw === p && x.on);
      if (u) { onIn(u); return; }
      sErr(ex.message||"Sign in failed — please try again"); sLd(false);
    }
  };

  const goSignUp = async () => {
    sErr(""); sLd(true); setSuccess("");
    if (!nm.trim()) { sErr("Name is required"); sLd(false); return; }
    if (!org.trim()) { sErr("Company name is required"); sLd(false); return; }
    if (!e.trim()||!e.includes("@")) { sErr("Valid email is required"); sLd(false); return; }
    if (p.length < 6) { sErr("Password must be at least 6 characters"); sLd(false); return; }
    try {
      if (supabase) {
        const { data: authData, error: authErr } = await supabase.auth.signUp({ email: e, password: p, options:{data:{full_name:nm.trim()}} });
        if (authErr) { sErr(authErr.message||"Signup failed — try a different email"); sLd(false); return; }
        if (!authData?.user) { sErr("Signup failed — no user returned. Try again."); sLd(false); return; }
        if (authData.user.identities && authData.user.identities.length === 0) {
          sErr("An account with this email already exists. Try signing in instead."); sLd(false); return;
        }
        const needsConfirm = !authData.session && authData.user.id;
        const slug = org.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/,'');
        const initials = nm.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
        const { error: rpcErr } = await supabase.rpc('create_org_and_profile', {
          p_user_id: authData.user.id, p_org_name: org.trim(),
          p_org_slug: slug + '-' + Date.now().toString(36),
          p_name: nm.trim(), p_email: e, p_initials: initials
        });
        if (rpcErr) {
          console.warn("RPC create_org_and_profile failed, using manual fallback:", rpcErr.message);
          const orgSlug = slug + '-' + Date.now().toString(36);
          const { data: orgData, error: orgErr } = await supabase.from('organizations').insert({name: org.trim(), slug: orgSlug}).select().single();
          if (orgErr) { sErr("Failed to create organization: " + orgErr.message); sLd(false); return; }
          const { error: profErr } = await supabase.from('profiles').insert({
            id: authData.user.id, org_id: orgData.id, name: nm.trim(),
            email: e, initials, role: 'admin', is_owner: true
          });
          if (profErr) { sErr("Account created but profile setup failed. Try signing in."); sLd(false); return; }
        }
        if (needsConfirm) {
          setSuccess("Account created! Check your email to confirm, then sign in.");
          setTab("in"); sLd(false); return;
        }
        setNewSignupUser({authUser: authData.user, orgName: org.trim()});
        sLd(false); return;
      }
      const initials = nm.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
      const newUser = { id: "U-"+Date.now(), name: nm.trim(), email: e, pw: p, role: "admin", av: initials, on: true };
      if (typeof onLocalSignUp === "function") onLocalSignUp(newUser, org.trim());
      setSuccess("Account created! Sign in with your email and password.");
      setTab("in"); sLd(false);
    } catch (ex) {
      sErr("Signup error: " + (ex.message||"Unknown error. Please try again."));
      sLd(false);
    }
  };

  const go = () => tab === "in" ? goSignIn() : goSignUp();

  const inputStyle = {width:"100%",padding:"12px 14px",borderRadius:12,border:"1px solid #E2E8F0",fontSize:14,color:"#0F172A",background:"#FFF",outline:"none",fontFamily:"'Outfit',system-ui,sans-serif",transition:"border-color .2s,box-shadow .2s"};
  const inputFocus = "input:focus{border-color:#2563EB!important;box-shadow:0 0 0 3px rgba(37,99,235,.1)!important}";

  // Step 2: org setup after new signup
  if (newSignupUser) {
    return <OrgSetupModal
      user={{org_id: null}} // org was just created via RPC, no org_id available yet
      isNewSignup={true}
      prefillName={newSignupUser.orgName}
      onDone={(fields)=>{
        // Sign in automatically and pass org fields along
        supabase?.auth.signInWithPassword({email:e,password:p}).then(async({data,error})=>{
          if(!error&&data?.user){
            const profile = await getProfile();
            if(profile){
              // Persist extra fields to org if we have org_id now
              if(supabase&&profile.org_id&&(fields.industry||fields.country||fields.size)){
                const upd={updated_at:new Date().toISOString()};
                if(fields.industry)upd.industry=fields.industry;
                if(fields.country)upd.country=fields.country;
                if(fields.size)upd.size=fields.size;
                supabase.from('organizations').update(upd).eq('id',profile.org_id).then(()=>{}).catch(()=>{});
              }
              onSupaIn&&onSupaIn(data.user, profile);
            }
          }
        }).catch(()=>{ sErr("Auto sign-in failed. Please sign in manually."); setTab("in"); sLd(false); });
      }}
    />;
  }

  return (
    <div style={{minHeight:"100vh",background:"#FAFBFD",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit',system-ui,sans-serif",color:"#0F172A",position:"relative",overflow:"hidden"}}>
      <style>{inputFocus+"@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');@keyframes loginFade{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}"}</style>

      {/* Pattern background - scattered logistics icons */}
      <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none"}}>
        {/* Gradient overlay */}
        <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse at 50% 50%,rgba(252,253,255,.85) 20%,rgba(250,251,253,.5) 70%)",zIndex:1}}/>
        {/* SVG pattern icons scattered */}
        <svg style={{position:"absolute",inset:0,width:"100%",height:"100%",opacity:.28}} xmlns="http://www.w3.org/2000/svg">
          {/* Spreadsheet */}
          <g transform="translate(80,60) rotate(-8)"><rect x="0" y="0" width="80" height="60" rx="4" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="0" y1="15" x2="80" y2="15" stroke="#475569" strokeWidth="1"/><line x1="0" y1="30" x2="80" y2="30" stroke="#475569" strokeWidth="1"/><line x1="0" y1="45" x2="80" y2="45" stroke="#475569" strokeWidth="1"/><line x1="25" y1="0" x2="25" y2="60" stroke="#475569" strokeWidth="1"/><line x1="55" y1="0" x2="55" y2="60" stroke="#475569" strokeWidth="1"/></g>
          {/* Document */}
          <g transform="translate(920,80) rotate(5)"><rect x="0" y="0" width="55" height="70" rx="3" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="10" y1="20" x2="45" y2="20" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><line x1="10" y1="30" x2="40" y2="30" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><line x1="10" y1="40" x2="35" y2="40" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><line x1="10" y1="50" x2="28" y2="50" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/></g>
          {/* Chart bars */}
          <g transform="translate(1050,400) rotate(-3)"><rect x="0" y="40" width="12" height="30" rx="2" fill="#475569" opacity=".5"/><rect x="18" y="25" width="12" height="45" rx="2" fill="#475569" opacity=".5"/><rect x="36" y="10" width="12" height="60" rx="2" fill="#475569" opacity=".5"/><rect x="54" y="30" width="12" height="40" rx="2" fill="#475569" opacity=".5"/><line x1="0" y1="70" x2="66" y2="70" stroke="#475569" strokeWidth="1.5"/></g>
          {/* Shipping box */}
          <g transform="translate(150,500) rotate(6)"><rect x="0" y="10" width="60" height="50" rx="3" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="0" y1="25" x2="60" y2="25" stroke="#475569" strokeWidth="1"/><line x1="30" y1="25" x2="30" y2="60" stroke="#475569" strokeWidth="1"/><path d="M0 10 L30 0 L60 10" fill="none" stroke="#475569" strokeWidth="1.5"/></g>
          {/* Checklist */}
          <g transform="translate(1100,180) rotate(-4)"><rect x="0" y="0" width="60" height="70" rx="4" fill="none" stroke="#475569" strokeWidth="1.5"/><rect x="10" y="12" width="10" height="10" rx="2" fill="none" stroke="#475569" strokeWidth="1.5"/><path d="M12 17 L16 21 L24 13" stroke="#475569" strokeWidth="1.5" fill="none" strokeLinecap="round"/><line x1="26" y1="17" x2="50" y2="17" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><rect x="10" y="30" width="10" height="10" rx="2" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="26" y1="35" x2="50" y2="35" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><rect x="10" y="48" width="10" height="10" rx="2" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="26" y1="53" x2="45" y2="53" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/></g>
          {/* Globe/shipping route */}
          <g transform="translate(60,350) rotate(3)"><circle cx="35" cy="35" r="30" fill="none" stroke="#475569" strokeWidth="1.5"/><ellipse cx="35" cy="35" rx="15" ry="30" fill="none" stroke="#475569" strokeWidth="1"/><line x1="5" y1="25" x2="65" y2="25" stroke="#475569" strokeWidth="1"/><line x1="5" y1="45" x2="65" y2="45" stroke="#475569" strokeWidth="1"/></g>
          {/* Clock */}
          <g transform="translate(800,520) rotate(-6)"><circle cx="28" cy="28" r="25" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="28" y1="12" x2="28" y2="28" stroke="#475569" strokeWidth="2" strokeLinecap="round"/><line x1="28" y1="28" x2="40" y2="34" stroke="#475569" strokeWidth="2" strokeLinecap="round"/></g>
          {/* Contact card */}
          <g transform="translate(600,80) rotate(4)"><rect x="0" y="0" width="70" height="45" rx="4" fill="none" stroke="#475569" strokeWidth="1.5"/><circle cx="20" cy="18" r="8" fill="none" stroke="#475569" strokeWidth="1.5"/><line x1="36" y1="12" x2="60" y2="12" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><line x1="36" y1="22" x2="55" y2="22" stroke="#475569" strokeWidth="1.5" strokeLinecap="round"/><line x1="10" y1="35" x2="60" y2="35" stroke="#475569" strokeWidth="1" strokeLinecap="round"/></g>
          {/* Gear */}
          <g transform="translate(400,550) rotate(12)"><circle cx="20" cy="20" r="8" fill="none" stroke="#475569" strokeWidth="1.5"/><circle cx="20" cy="20" r="3" fill="#475569" opacity=".4"/><line x1="20" y1="4" x2="20" y2="9" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="20" y1="31" x2="20" y2="36" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="4" y1="20" x2="9" y2="20" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="31" y1="20" x2="36" y2="20" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="9" y1="9" x2="13" y2="13" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="27" y1="27" x2="31" y2="31" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="9" y1="31" x2="13" y2="27" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/><line x1="27" y1="13" x2="31" y2="9" stroke="#475569" strokeWidth="2.5" strokeLinecap="round"/></g>
          {/* Small envelope */}
          <g transform="translate(950,480) rotate(-10)"><rect x="0" y="5" width="50" height="35" rx="3" fill="none" stroke="#475569" strokeWidth="1.5"/><path d="M0 8 L25 25 L50 8" fill="none" stroke="#475569" strokeWidth="1.5"/></g>
          {/* Arrow/flow */}
          <g transform="translate(300,120) rotate(15)"><line x1="0" y1="20" x2="50" y2="20" stroke="#475569" strokeWidth="2" strokeLinecap="round"/><path d="M42 12 L52 20 L42 28" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></g>
          {/* Pie chart */}
          <g transform="translate(750,200) rotate(-8)"><circle cx="25" cy="25" r="22" fill="none" stroke="#475569" strokeWidth="1.5"/><path d="M25 3 A22 22 0 0 1 47 25 L25 25 Z" fill="#475569" opacity=".15"/><line x1="25" y1="25" x2="25" y2="3" stroke="#475569" strokeWidth="1.5"/><line x1="25" y1="25" x2="47" y2="25" stroke="#475569" strokeWidth="1.5"/></g>
        </svg>
      </div>

      {/* Login card */}
      <div style={{position:"relative",zIndex:10,background:"rgba(255,255,255,.92)",backdropFilter:"blur(20px) saturate(1.4)",WebkitBackdropFilter:"blur(20px) saturate(1.4)",borderRadius:24,border:"1px solid rgba(226,232,240,.8)",padding:"44px 40px",width:440,boxShadow:"0 24px 64px rgba(0,0,0,.06),0 1px 3px rgba(0,0,0,.04)",animation:"loginFade .6s cubic-bezier(.16,1,.3,1)"}}>

        {/* Back button */}
        {onBack&&<button onClick={onBack} style={{position:"absolute",top:16,left:16,background:"rgba(241,245,249,.8)",border:"1px solid #E2E8F0",color:"#64748B",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:4,padding:"6px 12px",borderRadius:8}}>← Back</button>}

        {/* Logo */}
        <div style={{textAlign:"center",marginBottom:28}}>
          <svg width="64" height="64" viewBox="0 0 36 36" fill="none" style={{marginBottom:16}}><defs><linearGradient id="pk3" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#2563EB"/><stop offset="100%" stopColor="#0EA5E9"/></linearGradient></defs><path d="M22 7 C22 7 10 7 10 14 C10 21 26 17 26 23 C26 29 12 29 12 29" stroke="#c7d8f0" strokeWidth="6" strokeLinecap="round" fill="none"/><path d="M22 6 C22 6 10 6 10 13 C10 20 26 16 26 22 C26 28 12 28 12 28" stroke="url(#pk3)" strokeWidth="4.5" strokeLinecap="round" fill="none"/><circle cx="28" cy="8" r="5" fill="#c7d8f0"/><circle cx="28" cy="8" r="4" fill="#34D399"/></svg>
          <h1 style={{fontSize:28,fontWeight:800,margin:0,fontFamily:HF,letterSpacing:"-.02em"}}><span style={{background:"linear-gradient(90deg,#3B82F6,#06B6D4)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>Smart</span><span style={{color:"#0F172A"}}>TraQ</span></h1>
          <p style={{fontSize:13,color:"#94A3B8",marginTop:5,fontWeight:500}}>Trade & Logistics Intelligence</p>
        </div>

        {/* Tabs */}
        {hideSignUp?(
          <div style={{textAlign:"center",marginBottom:20}}><span style={{fontSize:14,fontWeight:600,color:"#2563EB"}}>Member Sign In</span></div>
        ):(
        <div style={{display:"flex",gap:0,marginBottom:20,borderRadius:12,overflow:"hidden",background:"#F1F5F9",padding:3}}>
          <button onClick={()=>{setTab("in");sErr("");setSuccess("")}} style={{flex:1,padding:"10px 0",fontSize:13,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",borderRadius:10,background:tab==="in"?"#FFF":"transparent",color:tab==="in"?"#2563EB":"#94A3B8",boxShadow:tab==="in"?"0 1px 3px rgba(0,0,0,.08)":"none",transition:"all .2s"}}>Sign In</button>
          <button onClick={()=>{setTab("up");sErr("");setSuccess("")}} style={{flex:1,padding:"10px 0",fontSize:13,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",borderRadius:10,background:tab==="up"?"#FFF":"transparent",color:tab==="up"?"#2563EB":"#94A3B8",boxShadow:tab==="up"?"0 1px 3px rgba(0,0,0,.08)":"none",transition:"all .2s"}}>Sign Up</button>
        </div>
        )}

        {/* Fields */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {tab==="up" && <div><label style={{fontSize:11,fontWeight:600,color:"#64748B",display:"block",marginBottom:5,letterSpacing:".3px"}}>FULL NAME</label><input value={nm} onChange={x=>sNm(x.target.value)} style={inputStyle} placeholder="Enter your name"/></div>}
          {tab==="up" && <div><label style={{fontSize:11,fontWeight:600,color:"#64748B",display:"block",marginBottom:5,letterSpacing:".3px"}}>COMPANY NAME</label><input value={org} onChange={x=>sOrg(x.target.value)} style={inputStyle} placeholder="Your Trading Company"/></div>}
          <div><label style={{fontSize:11,fontWeight:600,color:"#64748B",display:"block",marginBottom:5,letterSpacing:".3px"}}>EMAIL</label><input value={e} onChange={x=>sE(x.target.value)} onKeyDown={x=>x.key==="Enter"&&go()} style={inputStyle} placeholder="your@email.com" type="email"/></div>
          <div><label style={{fontSize:11,fontWeight:600,color:"#64748B",display:"block",marginBottom:5,letterSpacing:".3px"}}>PASSWORD</label><input value={p} onChange={x=>sP(x.target.value)} onKeyDown={x=>x.key==="Enter"&&go()} style={inputStyle} placeholder={tab==="up"?"Min 6 characters":"Enter your password"} type="password"/></div>
        </div>

        {/* Errors / success */}
        {err && <div style={{fontSize:12,color:"#DC2626",marginTop:12,padding:"8px 12px",borderRadius:10,background:"#FEF2F2",border:"1px solid #FECACA",fontWeight:500}}>{err}</div>}
        {success && <div style={{fontSize:12,color:"#059669",marginTop:12,padding:"8px 12px",borderRadius:10,background:"#F0FDF4",border:"1px solid #A7F3D0",fontWeight:500}}>{success}</div>}

        {/* Submit */}
        <button onClick={go} disabled={ld} style={{width:"100%",padding:"13px 0",marginTop:16,borderRadius:12,border:"none",background:"linear-gradient(135deg,#2563EB,#0891B2)",color:"#FFF",cursor:"pointer",fontSize:15,fontWeight:700,fontFamily:"inherit",boxShadow:"0 4px 14px rgba(37,99,235,.25)",opacity:ld?0.6:1,transition:"all .2s"}}>
          {ld ? <span style={{display:"inline-flex",alignItems:"center",gap:8}}><span style={{width:16,height:16,border:"2.5px solid rgba(255,255,255,.3)",borderTopColor:"#FFF",borderRadius:"50%",animation:"spin .6s linear infinite",display:"inline-block"}}/>{tab==="in"?"Signing in...":"Creating account..."}</span> : tab==="in"?"Sign In":"Create Account"}
        </button>

        {/* ── Google OAuth ── */}
        {supabase&&<>
          <div style={{display:"flex",alignItems:"center",gap:12,margin:"16px 0 0"}}>
            <div style={{flex:1,height:1,background:"#E2E8F0"}}/><span style={{fontSize:11,color:"#94A3B8",fontWeight:500}}>or</span><div style={{flex:1,height:1,background:"#E2E8F0"}}/>
          </div>
          <button onClick={async()=>{sErr("");sLd(true);try{const{error}=await supabase.auth.signInWithOAuth({provider:"google",options:{redirectTo:window.location.origin+window.location.pathname}});if(error){sErr(error.message||"Google sign-in failed");sLd(false)}}catch(ex){sErr("Google sign-in failed");sLd(false)}}} disabled={ld} style={{width:"100%",padding:"12px 0",marginTop:12,borderRadius:12,border:"1px solid #E2E8F0",background:"#FFF",color:"#334155",cursor:"pointer",fontSize:14,fontWeight:600,fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:10,boxShadow:"0 1px 3px rgba(0,0,0,.06)",transition:"all .2s"}}>
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.42l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>
        </>}

        {/* Forgot password */}
        {tab==="in"&&<div style={{textAlign:"center",marginTop:12}}>
          <button onClick={async()=>{
            if(!e.trim()||!e.includes("@")){sErr("Enter your email above first");return}
            sErr("");sLd(true);
            if(supabase){const{error:resetErr}=await supabase.auth.resetPasswordForEmail(e.trim(),{redirectTo:window.location.origin});if(resetErr){sErr(resetErr.message||"Failed to send reset email")}else{setSuccess("Password reset link sent to "+e.trim()+" — check your inbox (and spam folder). Click the link to set a new password.")}};
            sLd(false);
          }} style={{background:"transparent",border:"none",color:"#64748B",cursor:"pointer",fontSize:12,fontWeight:500,fontFamily:"inherit",textDecoration:"underline",padding:4}}>Forgot password?</button>
        </div>}

        {/* Beta badge */}
        <div style={{marginTop:16,textAlign:"center"}}>
          <span style={{fontSize:12,color:"#94A3B8",fontWeight:500}}>🚀 Free during Beta · No credit card</span>
        </div>
      </div>
    </div>
  );
}

// ── View Order (comments, doc checklist, auto-stage) ──

export { JoinForm, LoginPage };
